import { CollectionData, LinkData } from "interfaces";
import { TFunction } from "next-i18next/pages";

// Custom URL comparator to ignore trailing slashes.
const urlComparator = (
  url1: string | undefined,
  url2: string | undefined
): boolean => {
  if (url1?.endsWith("/")) {
    url1 = url1.slice(0, -1);
  }
  if (url2?.endsWith("/")) {
    url2 = url2.slice(0, -1);
  }
  return !!(url1 && url2) && url1 === url2;
};

/**
 * A collection is a search results feed when its own url
 * shares a pathname with its search description url
 */
export function isSearchResultsCollection(
  collection?: CollectionData
): boolean {
  if (!collection?.url || !collection?.searchDataUrl) return false;
  // Palace CM currently always provides absolute collection and search URLs
  try {
    const { pathname: collectionPathname } = new URL(collection.url);
    const { pathname: searchDataUrlPathname } = new URL(
      collection.searchDataUrl
    );
    return urlComparator(collectionPathname, searchDataUrlPathname);
  } catch {
    return false;
  }
}

/**
 * The CM titles every search results feed "Search" (OpenSearchDocument.search_info).
 * Every other collection title is library data and is shown as-is.
 * Without a `t` we fall back to the CM's English.
 */
export function collectionTitleText(
  collection: CollectionData,
  t?: TFunction
): string {
  if (!t || !isSearchResultsCollection(collection)) return collection.title;
  return t("collection.searchResults", "Search");
}

const computeBreadcrumbs = (
  collection?: CollectionData,
  t?: TFunction
): LinkData[] => {
  let links: LinkData[] = [];

  // Currently, search runs across the entire catalog.
  // So no matter where search originates (home, lane, my books, etc.),
  // the breadcrumbs should be uniform: Catalog Root / Search
  if (collection && isSearchResultsCollection(collection)) {
    return hierarchyComputeBreadcrumbs(
      { ...collection, parentLink: undefined },
      urlComparator,
      t
    );
  }

  if (
    collection &&
    collection.raw &&
    collection.raw["simplified:breadcrumbs"] &&
    collection.raw["simplified:breadcrumbs"][0] &&
    collection.raw["simplified:breadcrumbs"][0].link
  ) {
    const rawLinks = collection.raw["simplified:breadcrumbs"][0].link;
    links = rawLinks.map((link: any) => {
      return {
        url: link["$"].href.value,
        text: link["$"].title.value
      };
    });
    links.push({
      url: collection.url,
      text: collectionTitleText(collection, t)
    });
  } else {
    links = hierarchyComputeBreadcrumbs(collection, urlComparator, t);
  }

  return links;
};

export default computeBreadcrumbs;

/**
 * Computes breadcrumbs assuming that the OPDS feed is hierarchical - uses
 * the catalog root link, the parent of the current collection if it's not
 * the root, and the current collection. The OPDS spec doesn't require a
 * hierarchy, so this may not make sense for some feeds.
 * */
export function hierarchyComputeBreadcrumbs(
  collection?: CollectionData,
  comparator?: (url1: string, url2: string) => boolean,
  t?: TFunction
): LinkData[] {
  const links: LinkData[] = [];

  if (!collection) {
    return [];
  }

  if (!comparator) {
    comparator = (url1, url2) => url1 === url2;
  }

  const { catalogRootLink, parentLink } = collection;

  if (catalogRootLink && !comparator(catalogRootLink.url, collection.url)) {
    links.push({
      text:
        catalogRootLink.text ||
        (t ? t("computeBreadcrumbs.catalog", "Catalog") : "Catalog"),
      url: catalogRootLink.url
    });
  }

  if (
    parentLink &&
    parentLink.url &&
    parentLink.text &&
    (!catalogRootLink || !comparator(parentLink.url, catalogRootLink.url)) &&
    !comparator(parentLink.url, collection.url)
  ) {
    links.push({
      text: parentLink.text,
      url: parentLink.url
    });
  }

  links.push({
    url: collection.url,
    text: collectionTitleText(collection, t)
  });

  return links;
}
